import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { API, apiGet } from "../api";
import { toast } from "sonner";
import { Download, X, Loader2, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { PriceFileGenerator } from "../components/PriceFileGenerator";
import usePageTitle from "../hooks/usePageTitle";
import { formatCurrency, formatMonthShort } from "../lib/format";

// Small pill shown against a carried-forward reference price: the price came from
// an earlier month's price file because this invoice month had no file for the SKU.
const CarriedBadge = ({ sourceMonth }) => (
  <span
    title={`Price carried forward from ${formatMonthShort(sourceMonth)} — no price file for this SKU in the invoice month`}
    style={{
      display: "inline-block", marginLeft: 6, padding: "0 5px", borderRadius: 4,
      fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.02em", verticalAlign: "middle",
      background: "rgba(255,176,32,0.14)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.35)",
      fontFamily: "system-ui, sans-serif",
    }}
  >
    ↩ {formatMonthShort(sourceMonth)}
  </span>
);

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [globalSummary, setGlobalSummary] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [exportFmt, setExportFmt] = useState("csv"); // csv | xlsx — applies to every export on this page
  const [activeTab, setActiveTab] = useState("consolidated");
  const abortRef = useRef(null);

  // ── On-screen report viewer state ──
  const [viewLoading, setViewLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewData, setViewData] = useState(null); // { rows, summary, total }
  const [viewError, setViewError] = useState("");
  const viewAbort = useRef(null);
  const [activeTile, setActiveTile] = useState("all");
  const [visibleCount, setVisibleCount] = useState(100);

  usePageTitle("Reports");
  useEffect(() => {
    viewAbort.current?.abort();
    setViewData(null); setViewError(""); setViewLoading(false); setLoadingMore(false);
  }, [filterSupplier, filterMonth, filterType]);
  useEffect(() => () => viewAbort.current?.abort(), []);

  useEffect(() => {
    const loadFilters = async () => {
      const [suppRes, monthRes] = await Promise.all([
        apiGet("/suppliers/upload-status"),
        apiGet("/invoices/months"),
      ]);
      if (suppRes?.suppliers) setSuppliers([...new Set(suppRes.suppliers.map(s => s.supplier))].sort());
      if (monthRes?.months) setAvailableMonths(monthRes.months);
    };
    loadFilters();
  }, []);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const downloadReport = useCallback(async () => {
    try {
      setLoading(true);
      setGlobalSummary(null);
      setDownloadProgress({ phase: "computing", pct: 0 });
      abortRef.current = new AbortController();

      let url = filterType === "missing"
        ? `${API}/reports/missing-prices-export`
        : `${API}/reports/download-csv`;
      const params = new URLSearchParams();
      if (filterSupplier !== "all") params.append("supplier_filter", filterSupplier);
      if (filterMonth !== "all") params.append("month_filter", filterMonth);
      if (filterType === "overcharges") params.append("only_overcharges", "true");
      params.append("fmt", exportFmt);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        signal: abortRef.current.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Read the summary header from X-Report-Summary
      const summaryHeader = response.headers.get("X-Report-Summary");
      if (summaryHeader) {
        try {
          setGlobalSummary(JSON.parse(summaryHeader));
        } catch { /* non-JSON header — ignore */ }
      }

      setDownloadProgress({ phase: "downloading", pct: 50 });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStr = new Date().toISOString().split("T")[0];
      const supplierStr = filterSupplier !== "all" ? `_${filterSupplier}` : "";
      const monthStr = filterMonth !== "all" ? `_${filterMonth}` : "";
      const typeStr = filterType === "overcharges" ? "_overcharges_only" : "";
      const ext = exportFmt === "xlsx" ? "xlsx" : "csv";
      a.href = downloadUrl;
      a.download = filterType === "missing"
        ? `missing_price_file_report${supplierStr}${monthStr}_${dateStr}.${ext}`
        : `price_comparison_report${supplierStr}${monthStr}${typeStr}_${dateStr}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);

      setDownloadProgress({ phase: "complete", pct: 100 });
      toast.success("Report downloaded successfully");
    } catch (error) {
      if (error.name === "AbortError") {
        toast.info("Report download cancelled");
      } else {
        toast.error("Failed to download report. Please try again.");
      }
      setDownloadProgress(null);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [filterSupplier, filterMonth, filterType, exportFmt]);

  // Latest price per (supplier, SKU) from uploaded price files — all suppliers,
  // one CSV. Independent of the report-type filters.
  const downloadLatestPrices = useCallback(async () => {
    try {
      setLoading(true);

      // xlsx: the backend builds the whole price book as one workbook (no
      // pagination), so just stream the file straight down.
      if (exportFmt === "xlsx") {
        const res = await fetch(`${API}/reports/latest-prices-export?fmt=xlsx`, { method: "GET" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `latest_prices_${new Date().toISOString().split("T")[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Latest prices exported to Excel");
        return;
      }

      // Page through so a large price book is delivered completely, then stitch
      // the CSV client-side.
      let after = 0;
      let guard = 0;
      const all = [];
      while (true) {
        const res = await fetch(`${API}/reports/latest-prices-export?after=${after}&limit=20000`, { method: "GET" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const page = await res.json();
        const rows = Array.isArray(page.rows) ? page.rows : [];
        all.push(...rows);
        if (page.done || rows.length === 0 || page.next_after === after) break;
        after = page.next_after;
        if (++guard > 1000) break;
      }
      const esc = (v) => {
        let s = v === null || v === undefined ? "" : String(v);
        if (typeof v === "string" && (/^[\s\x00-\x1f\x7f\ufeff]*[=+@-]/.test(s) || /^[\t\r\n]/.test(s))) s = "'" + s;
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = "Supplier,SKU,Description,Latest Price,Date the latest price covered\n";
      const body = all
        .map((r) => [r.supplier, r.sku, r.description, r.latest_price, r.date_covered].map(esc).join(","))
        .join("\n");
      const blob = new Blob([header + body + "\n"], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `latest_prices_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Latest prices exported — ${all.length.toLocaleString()} rows`);
    } catch (e) {
      toast.error("Failed to export latest prices");
    } finally {
      setLoading(false);
    }
  }, [exportFmt]);

  // ── On-screen report viewer logic ──
  const VIEW_PAGE = 2000;
  const VIEW_STEP = 100;

  const generateReport = useCallback(async () => {
    viewAbort.current?.abort();
    const controller = new AbortController(); viewAbort.current = controller;
    setViewLoading(true);
    setViewError("");
    setViewData(null);
    setActiveTile("all");
    setVisibleCount(VIEW_STEP);
    try {
      const params = new URLSearchParams({ skip: "0", limit: String(VIEW_PAGE), report_type: filterType });
      if (filterSupplier !== "all") params.append("supplier_filter", filterSupplier);
      if (filterMonth !== "all") params.append("month_filter", filterMonth);
      const res = await fetch(`${API}/monthly-consolidated-report?${params.toString()}`, { method: "POST", credentials: "include", signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setViewData({
        rows: data.report || [],
        summary: data.global_summary || null,
        total: data.pagination?.total || 0,
      });
    } catch (error) {
      if (error.name === "AbortError") return;
      setViewError("The report could not be generated. Your selected filters are unchanged; please try again.");
      toast.error("Failed to generate report");
    } finally {
      if (!controller.signal.aborted) setViewLoading(false);
    }
  }, [filterSupplier, filterMonth, filterType]);

  const loadMoreRows = useCallback(async () => {
    if (!viewData) return;
    setLoadingMore(true);
    const controller = new AbortController(); viewAbort.current = controller;
    try {
      const params = new URLSearchParams({ skip: String(viewData.rows.length), limit: String(VIEW_PAGE), report_type: filterType });
      if (filterSupplier !== "all") params.append("supplier_filter", filterSupplier);
      if (filterMonth !== "all") params.append("month_filter", filterMonth);
      const res = await fetch(`${API}/monthly-consolidated-report?${params.toString()}`, { method: "POST", credentials: "include", signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setViewData(v => ({ ...v, rows: [...v.rows, ...(data.report || [])] }));
    } catch (error) {
      if (error.name === "AbortError") return;
      setViewError("Could not load additional rows. Already loaded results are still shown.");
      toast.error("Failed to load more rows");
    } finally {
      if (!controller.signal.aborted) setLoadingMore(false);
    }
  }, [viewData, filterSupplier, filterMonth, filterType]);

  const tileMatches = useCallback((r, tile) => {
    if (tile === "matched") return r.has_price_file;
    if (tile === "disc" || tile === "value") return r.has_discrepancy;
    if (tile === "missing") return !r.has_price_file;
    return true;
  }, []);

  // Rows for the drill-down table: filtered by active tile; the Overcharge
  // tile additionally sorts biggest-first.
  const viewRows = useMemo(() => {
    if (!viewData) return [];
    let rows = viewData.rows.filter(r => tileMatches(r, activeTile));
    if (activeTile === "value") {
      rows = [...rows].sort((a, b) => (b.overcharge_amount || 0) - (a.overcharge_amount || 0));
    }
    return rows;
  }, [viewData, activeTile, tileMatches]);

  const selectTile = (tile) => { setActiveTile(tile); setVisibleCount(VIEW_STEP); };

  return (
    <div data-testid="reports-page">
      {viewError && <div role="alert" data-testid="report-load-error" className="page-read-error">{viewError}</div>}
      <div className="page-header">
        <h1 className="page-title">Reports & Analysis</h1>
        <p className="page-subtitle">Generate an interactive report on screen, or download it as CSV · transport/freight exclusion configurable in Settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="consolidated">Price Comparison Report</TabsTrigger>
          <TabsTrigger value="generator">Price File Generator</TabsTrigger>
        </TabsList>

        <TabsContent value="consolidated">
          {/* Report Settings */}
          <div className="card" style={{ marginBottom: "2rem", marginTop: "2rem" }}>
            <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "1.25rem", color: "var(--octopus-ice)" }}>
              Report Settings
            </h2>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Filter by Month</label>
                <Select value={filterMonth} onValueChange={(val) => setFilterMonth(val)}>
                  <SelectTrigger className="form-input" data-testid="filter-month"
                    style={{ height: "auto", padding: "0.6rem 0.75rem", border: "1px solid rgba(96, 240, 248, 0.2)", background: "rgba(10, 18, 42, 0.8)", color: filterMonth !== "all" ? "var(--octopus-ice)" : "var(--octopus-voltage)" }}
                  >
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent style={{ background: "rgba(10, 18, 42, 0.98)", border: "1px solid rgba(96, 240, 248, 0.2)", backdropFilter: "blur(12px)" }}>
                    <SelectItem value="all" style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>All Months</SelectItem>
                    {availableMonths.map(m => <SelectItem key={m} value={m} style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Filter by Supplier</label>
                <Select value={filterSupplier} onValueChange={(val) => setFilterSupplier(val)}>
                  <SelectTrigger className="form-input" data-testid="filter-supplier"
                    style={{ height: "auto", padding: "0.6rem 0.75rem", border: "1px solid rgba(96, 240, 248, 0.2)", background: "rgba(10, 18, 42, 0.8)", color: filterSupplier !== "all" ? "var(--octopus-ice)" : "var(--octopus-voltage)" }}
                  >
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent style={{ background: "rgba(10, 18, 42, 0.98)", border: "1px solid rgba(96, 240, 248, 0.2)", backdropFilter: "blur(12px)" }}>
                    <SelectItem value="all" style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>All Suppliers</SelectItem>
                    {suppliers.map(s => <SelectItem key={s} value={s} style={{ color: "var(--octopus-ice)", cursor: "pointer" }}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Report Type Toggle */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">Report Type</label>
              <div data-testid="report-type-toggle" style={{
                display: "flex", flexWrap: "wrap", width: "fit-content", maxWidth: "100%", borderRadius: "8px", overflow: "hidden",
                border: "1px solid rgba(96, 240, 248, 0.2)"
              }}>
                <label
                  data-testid="filter-type-all"
                  style={{
                    padding: "0.5rem 1.25rem", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    fontSize: "0.875rem", fontWeight: "600", transition: "all 0.15s ease",
                    background: filterType === "all" ? "rgba(96, 240, 248, 0.15)" : "transparent",
                    color: filterType === "all" ? "var(--octopus-ice)" : "var(--octopus-voltage)",
                    borderRight: "1px solid rgba(96, 240, 248, 0.2)"
                  }}
                >
                  <input
                    type="radio" name="reportType" value="all"
                    checked={filterType === "all"}
                    onChange={() => setFilterType("all")}
                    style={{ display: "none" }}
                  />
                  All Items
                </label>
                <label
                  data-testid="filter-type-overcharges"
                  style={{
                    padding: "0.5rem 1.25rem", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    fontSize: "0.875rem", fontWeight: "600", transition: "all 0.15s ease",
                    background: filterType === "overcharges" ? "rgba(240, 80, 248, 0.15)" : "transparent",
                    color: filterType === "overcharges" ? "#F050F8" : "var(--octopus-voltage)",
                    borderRight: "1px solid rgba(96, 240, 248, 0.2)"
                  }}
                >
                  <input
                    type="radio" name="reportType" value="overcharges"
                    checked={filterType === "overcharges"}
                    onChange={() => setFilterType("overcharges")}
                    style={{ display: "none" }}
                  />
                  Only Overcharges
                </label>
                <label
                  data-testid="filter-type-missing"
                  style={{
                    padding: "0.5rem 1.25rem", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    fontSize: "0.875rem", fontWeight: "600", transition: "all 0.15s ease",
                    background: filterType === "missing" ? "rgba(255, 216, 79, 0.15)" : "transparent",
                    color: filterType === "missing" ? "#FFD84F" : "var(--octopus-voltage)"
                  }}
                >
                  <input
                    type="radio" name="reportType" value="missing"
                    checked={filterType === "missing"}
                    onChange={() => setFilterType("missing")}
                    style={{ display: "none" }}
                  />
                  Missing Price File
                </label>
              </div>
              {filterType === "missing" && (
                <p style={{ color: "var(--octopus-voltage)", fontSize: "0.78rem", opacity: 0.75, margin: "0.5rem 0 0" }}>
                  Every invoice line with no matching price file — one row per instance, with the reason
                  (no files for supplier, none for that month, or SKU not on file).
                </p>
              )}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
              <Button
                data-testid="generate-report-btn"
                onClick={generateReport}
                disabled={viewLoading || loading}
                className="btn btn-primary"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
              >
                {viewLoading ? (
                  <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Generating…</>
                ) : (
                  <><FileText size={16} /> Generate Report</>
                )}
              </Button>

              <Button
                data-testid="download-report-btn"
                onClick={downloadReport}
                disabled={loading || viewLoading}
                variant="outline"
                className="btn btn-secondary"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
              >
                {loading ? (
                  <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Preparing {exportFmt === "xlsx" ? "Excel" : "CSV"}…</>
                ) : (
                  <><Download size={16} /> Download {exportFmt === "xlsx" ? "Excel" : "CSV"}</>
                )}
              </Button>

              <Button
                data-testid="latest-prices-btn"
                onClick={downloadLatestPrices}
                disabled={loading || viewLoading}
                variant="outline"
                className="btn btn-secondary"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
                title="Latest price per SKU across all suppliers, from uploaded price files"
              >
                <Download size={16} /> Latest Prices {exportFmt === "xlsx" ? "Excel" : "CSV"}
              </Button>

              {/* Format switch — applies to every export on this page */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", marginLeft: "0.25rem" }}>
                <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>Format</span>
                {["csv", "xlsx"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setExportFmt(k)}
                    disabled={loading || viewLoading}
                    data-testid={`reports-fmt-${k}`}
                    className={exportFmt === k ? "btn btn-primary" : "btn btn-secondary"}
                    style={{ padding: "0.35rem 0.9rem", fontSize: "0.8rem" }}
                  >
                    {k === "xlsx" ? "Excel" : "CSV"}
                  </button>
                ))}
              </div>

              {loading && (
                <Button onClick={handleCancel} variant="outline" size="sm" data-testid="cancel-report-btn" style={{
                  borderColor: "var(--octopus-pink)", color: "var(--octopus-pink)"
                }}>
                  <X size={14} style={{ marginRight: "0.4rem" }} /> Cancel
                </Button>
              )}
            </div>
          </div>

          {/* ── On-screen report: clickable tiles + drill-down table ── */}
          {viewData && viewData.summary && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "1rem", marginBottom: "1rem" }} data-testid="report-tiles">
                {[
                  { tile: "all", label: "Invoices Analyzed", value: (viewData.summary.total || 0).toLocaleString(), color: "var(--octopus-voltage)" },
                  { tile: "matched", label: "Price Matched", value: (viewData.summary.matched || 0).toLocaleString(), color: "var(--octopus-green)" },
                  { tile: "disc", label: "Discrepancies", value: (viewData.summary.discrepancies || 0).toLocaleString(), color: "var(--octopus-pink)" },
                  { tile: "missing", label: "No Price File", value: Math.max((viewData.summary.total || 0) - (viewData.summary.matched || 0), 0).toLocaleString(), color: "#FFD84F" },
                  { tile: "value", label: "Total Overcharge", value: formatCurrency(viewData.summary.total_overcharge || 0), color: "#F050F8" },
                ].map((t) => (
                  <button
                    key={t.tile}
                    onClick={() => selectTile(t.tile)}
                    data-testid={`report-tile-${t.tile}`}
                    className="stat-card"
                    style={{
                      padding: "1.1rem", textAlign: "left", cursor: "pointer",
                      border: activeTile === t.tile ? "1px solid rgba(96,240,248,0.6)" : "1px solid transparent",
                      background: activeTile === t.tile ? "rgba(96,240,248,0.07)" : undefined,
                    }}
                    title="Click to drill into these rows"
                  >
                    <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", color: t.color, marginBottom: "0.45rem" }}>
                      {t.label}
                    </div>
                    <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "var(--octopus-ice)" }}>{t.value}</div>
                  </button>
                ))}
              </div>

              <div className="card" style={{ marginBottom: "2rem" }} data-testid="report-drilldown">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem", flexWrap: "wrap", gap: "0.5rem" }}>
                  <span style={{ fontSize: "0.85rem", color: "var(--octopus-ice)", fontWeight: 600 }}>
                    {activeTile === "all" ? "All rows" : activeTile === "matched" ? "Rows with a price file match"
                      : activeTile === "disc" ? "Rows with discrepancies" : activeTile === "missing" ? "Rows without a price file"
                      : "Discrepancies — biggest overcharge first"}
                  </span>
                  <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>
                    {viewRows.length.toLocaleString()} matching of {viewData.rows.length.toLocaleString()} loaded
                    {viewData.rows.length < viewData.total && ` (${viewData.total.toLocaleString()} total)`}
                  </span>
                </div>

                <div style={{ maxHeight: "480px", overflowY: "auto", border: "1px solid rgba(96,240,248,0.15)", borderRadius: "6px" }}>
                  <table className="data-table" data-testid="report-rows-table" style={{ marginBottom: 0, fontSize: "0.83rem" }}>
                    <thead style={{ position: "sticky", top: 0, background: "rgba(10,18,42,0.98)", zIndex: 1 }}>
                      <tr>
                        <th>Month</th>
                        <th>Supplier</th>
                        <th>SKU</th>
                        <th>Description</th>
                        <th style={{ textAlign: "right" }}>Qty</th>
                        <th style={{ textAlign: "right" }}>Invoiced</th>
                        <th style={{ textAlign: "right" }}>Price File</th>
                        <th style={{ textAlign: "right" }}>Overcharge</th>
                        <th>Invoice No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewRows.slice(0, visibleCount).map((r, i) => (
                        <tr key={`${r.invoice_number}-${r.product_code}-${i}`}>
                          <td style={{ whiteSpace: "nowrap" }}>{r.month}</td>
                          <td>{r.supplier}</td>
                          <td style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{r.product_code || "—"}</td>
                          <td style={{ maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.description}>{r.description || "—"}</td>
                          <td style={{ textAlign: "right", fontFamily: "monospace" }}>{r.quantity}</td>
                          <td style={{ textAlign: "right", fontFamily: "monospace" }}>{formatCurrency(r.invoice_price)}</td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", color: r.has_price_file ? "var(--octopus-ice)" : "var(--octopus-voltage)", opacity: r.has_price_file ? 1 : 0.55 }}>
                            {r.has_price_file ? formatCurrency(r.price_file_price) : "—"}
                            {r.has_price_file && r.price_basis === "carried_forward" && (
                              <CarriedBadge sourceMonth={r.price_source_month} />
                            )}
                          </td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: r.has_discrepancy ? "#F050F8" : "var(--octopus-voltage)" }}>
                            {r.has_discrepancy ? formatCurrency(r.overcharge_amount) : "—"}
                          </td>
                          <td style={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "nowrap" }}>{r.invoice_number || "—"}</td>
                        </tr>
                      ))}
                      {viewRows.length === 0 && (
                        <tr><td colSpan={9} style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>No rows in this view.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
                  {viewRows.length > visibleCount && (
                    <Button onClick={() => setVisibleCount(c => c + VIEW_STEP)} variant="outline" className="btn btn-secondary" data-testid="report-show-more">
                      Show {Math.min(VIEW_STEP, viewRows.length - visibleCount)} more ({(viewRows.length - visibleCount).toLocaleString()} remaining)
                    </Button>
                  )}
                  {viewData.rows.length < viewData.total && (
                    <Button onClick={loadMoreRows} disabled={loadingMore} variant="outline" className="btn btn-secondary" data-testid="report-load-more-server">
                      {loadingMore ? "Loading…" : `Load ${Math.min(VIEW_PAGE, viewData.total - viewData.rows.length).toLocaleString()} more from server`}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Loading progress */}
          {loading && downloadProgress && (
            <div className="card" style={{ marginBottom: "2rem", textAlign: "center", padding: "2rem" }}>
              <Loader2 size={28} style={{ color: "var(--octopus-voltage)", animation: "spin 1s linear infinite", marginBottom: "0.75rem" }} />
              <p style={{ color: "var(--octopus-ice)", fontWeight: "600", fontSize: "0.95rem", marginBottom: "0.5rem" }}>
                {downloadProgress.phase === "computing" ? "Computing report across all invoices..." : "Preparing download..."}
              </p>
              <p style={{ color: "var(--octopus-voltage)", fontSize: "0.8rem", opacity: 0.7 }}>
                This processes every invoice against your price files. Large datasets may take up to a minute.
              </p>
              <div style={{
                width: "100%", maxWidth: 320, margin: "1rem auto 0", height: 4, borderRadius: 2,
                background: "rgba(96, 240, 248, 0.1)", overflow: "hidden"
              }}>
                <div style={{
                  height: "100%", borderRadius: 2,
                  background: "linear-gradient(90deg, var(--octopus-voltage), var(--octopus-green))",
                  animation: "indeterminate 1.5s ease-in-out infinite",
                  width: "40%"
                }} />
              </div>
            </div>
          )}

          {/* Summary Stats — shown after download completes */}
          {!loading && globalSummary && (
            <>
              {globalSummary.filtered && (
                <div data-testid="filter-indicator" style={{
                  marginBottom: "1rem", padding: "0.6rem 1rem", borderRadius: "8px",
                  background: globalSummary.report_type === "missing_prices" ? "rgba(255, 216, 79, 0.08)" : "rgba(240, 80, 248, 0.08)",
                  border: `1px solid ${globalSummary.report_type === "missing_prices" ? "rgba(255, 216, 79, 0.25)" : "rgba(240, 80, 248, 0.2)"}`,
                  display: "inline-flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem"
                }}>
                  <span style={{ color: globalSummary.report_type === "missing_prices" ? "#FFD84F" : "#F050F8", fontWeight: "600" }}>Filtered:</span>
                  <span style={{ color: "var(--octopus-ice)" }}>
                    {globalSummary.report_type === "missing_prices"
                      ? `Showing ${(globalSummary.csv_rows || 0).toLocaleString()} invoice lines with no price file of ${(globalSummary.total || 0).toLocaleString()} analyzed`
                      : `Showing ${(globalSummary.csv_rows || 0).toLocaleString()} overcharge items of ${(globalSummary.total || 0).toLocaleString()} total invoices analyzed`}
                  </span>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", marginBottom: "2rem" }}>
                {(globalSummary.report_type === "missing_prices" ? [
                  { label: "Invoices Analyzed", value: (globalSummary.total || 0).toLocaleString(), color: "var(--octopus-voltage)" },
                  { label: "Missing Price Lines", value: (globalSummary.csv_rows || 0).toLocaleString(), color: "#FFD84F" },
                  { label: "Suppliers Affected", value: (globalSummary.suppliers_affected || 0).toLocaleString(), color: "var(--octopus-pink)" },
                  { label: "Spend Without Price File", value: formatCurrency(globalSummary.missing_spend || 0), color: "#F050F8" },
                ] : [
                  { label: "Invoices Analyzed", value: (globalSummary.total || 0).toLocaleString(), color: "var(--octopus-voltage)" },
                  { label: "Price Matched", value: (globalSummary.matched || 0).toLocaleString(), color: "var(--octopus-green)" },
                  { label: "Discrepancies", value: (globalSummary.discrepancies || 0).toLocaleString(), color: "var(--octopus-pink)" },
                  { label: "Total Overcharge", value: formatCurrency(globalSummary.total_overcharge || 0), color: "#F050F8" },
                ]).map((s) => (
                  <div key={s.label} className="stat-card" style={{ padding: "1.25rem" }}>
                    <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: s.color, marginBottom: "0.5rem" }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--octopus-ice)" }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Download confirmation */}
          {!loading && downloadProgress?.phase === "complete" && (
            <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
              <FileText size={36} style={{ color: "var(--octopus-green)", marginBottom: "0.75rem" }} />
              <p style={{ color: "var(--octopus-ice)", fontWeight: "600", fontSize: "1rem", marginBottom: "0.35rem" }}>
                Report downloaded successfully
              </p>
              <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", opacity: 0.7 }}>
                Check your downloads folder for the CSV file.
                {globalSummary && (globalSummary.report_type === "missing_prices"
                  ? ` Contains ${(globalSummary.csv_rows || 0).toLocaleString()} invoice lines with no price file (from ${(globalSummary.total || 0).toLocaleString()} analyzed).`
                  : globalSummary.filtered
                    ? ` Contains ${(globalSummary.csv_rows || 0).toLocaleString()} overcharge records (from ${(globalSummary.total || 0).toLocaleString()} invoices analyzed).`
                    : ` Contains ${(globalSummary.csv_rows || globalSummary.total || 0).toLocaleString()} invoice records.`
                )}
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="generator">
          <PriceFileGenerator />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
